import { Col, Row } from "antd";
import { useEffect, useRef, useState } from "react";
import ItemDetailsCard from "../../components/home/ItemDetailsCard";
import SummaryCard from "../../components/home/SummaryCard";
import PaymentCard from "../../components/home/payment/PaymentCard";
import useGetCategory from "../../functions/Category/getCategory";
import calculateTotalAmount from "../../functions/Common/calculateTotalAmount";
import useGetFakeAIImage from "../../functions/Fake/getFakeAIImage";
import useUploadFakeImage from "../../functions/Fake/uploadFakeImage";
import useGetMerchantExtraCharges from "../../functions/Merchant/getMerchantExtraCharges";
import useGetMerchantSST from "../../functions/Merchant/getMerchantSST";
import "../../styles/home/home.css";

const PLATE = {
  image_id: null,
  ai_image_data: null,
  items: [],
};

const INITIAL_RECEIPT = {
  total: 0,
  sst: 0,
  subtotal: 0,
  change: null,
  round_adj: null,
  cash: null,
  takeaway_fee: 0,
};

const TIMEOUT_DURATION = 10;
const BYPASS_IMAGE = true;

function HomePage() {
  const itemDetailsCardRef = useRef();
  const fakeImageTriggeredRef = useRef(false);

  const [plates, setPlates] = useState([PLATE]);
  const [selectedPlateIndex, setSelectedPlateIndex] = useState(0);
  const [activeTabKey, setActiveTabKey] = useState("2");
  const [selectedItemKey, setSelectedItemKey] = useState();
  const [isShowPayment, setIsShowPayment] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState();
  const [isDisableButton, setIsDisableButton] = useState({
    cancel_order_btn: false,
    pay_btn: false,
    delete_plate_btn: false,
    add_plate_btn: false,
    add_item_btn: false,
    takeaway_checkbox: false,
    delete_item_btn: false,
  });
  const [receiptPrice, setReceiptPrice] = useState(INITIAL_RECEIPT);
  const [extraChargesOptions, setExtraChargesOptions] = useState({
    takeaway_fee: false,
  });
  const [connectDevice, setConnectDevice] = useState(null);

  const { category, categoryLoading, getCategory } = useGetCategory();
  const { sstInfo, getMerchantSST } = useGetMerchantSST();
  const { extraCharges, getMerchantExtraCharges } =
    useGetMerchantExtraCharges();

  const { uploadFakeImage, uploadedImageId, setUploadedImageId } =
    useUploadFakeImage();
  const { getFakeAIImage } = useGetFakeAIImage();

  const getFakePlates = (cloned_plates) => {
    setPlates(cloned_plates);
    setIsDisableButton((prev) =>
      Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
    );
    setUploadedImageId(null);
    setActiveTabKey("2");
  };

  const callGetFakeImage = (image_id) => {
    getFakeAIImage(
      image_id,
      TIMEOUT_DURATION,
      plates,
      selectedPlateIndex,
      getFakePlates
    );
  };

  const createFakeImage = () => {
    if (!uploadedImageId) {
      uploadFakeImage("", TIMEOUT_DURATION, callGetFakeImage);
    } else {
      callGetFakeImage(uploadedImageId);
    }
  };

  const onPaymentSuccessReset = () => {
    setPlates([]);
    setIsShowPayment(false);
    setSelectedPlateIndex(0);
    setSelectedItemKey(null);
    setActiveTabKey("2");
    setReceiptPrice(INITIAL_RECEIPT);
    if (BYPASS_IMAGE) createFakeImage();
  };

  const onReloadDetails = () => {
    getCategory();
    getMerchantSST();
    getMerchantExtraCharges();
  };

  const handleAlaCartOrder = () => {
    createFakeImage();
  };

  useEffect(() => {
    const amount = calculateTotalAmount(
      plates,
      sstInfo,
      extraCharges,
      extraChargesOptions
    );
    setReceiptPrice((prev) => ({
      ...prev,
      total: amount.total,
      subtotal: amount.subtotal,
      sst: amount.sst,
    }));

    if (BYPASS_IMAGE) {
      const needsFakeImage =
        plates.length === 0 || plates[plates.length - 1]?.image_id === null;
      if (needsFakeImage && !fakeImageTriggeredRef.current) {
        fakeImageTriggeredRef.current = true;
        createFakeImage();
      } else if (!needsFakeImage) {
        fakeImageTriggeredRef.current = false;
      }
    }
  }, [plates, sstInfo, extraCharges, extraChargesOptions]);

  useEffect(() => {
    let amount = calculateTotalAmount(
      plates,
      sstInfo,
      extraCharges,
      extraChargesOptions
    );
    setReceiptPrice((prev) => ({
      ...prev,
      total: amount.total,
      subtotal: amount.subtotal,
      sst: amount.sst,
      takeaway_fee: extraChargesOptions.takeaway_fee
        ? extraCharges.takeaway_fee
        : 0,
    }));
  }, [extraChargesOptions]);

  return (
    <Row gutter={[10, 10]} style={{ height: "100%" }}>
      <Col span={16} style={{ height: "100%" }}>
        {!isShowPayment ? (
          <ItemDetailsCard
            plates={plates}
            setPlates={setPlates}
            itemDetailsCardRef={itemDetailsCardRef}
            selectedPlateIndex={selectedPlateIndex}
            activeTabKey={activeTabKey}
            setActiveTabKey={setActiveTabKey}
            selectedItemKey={selectedItemKey}
            setSelectedItemKey={setSelectedItemKey}
            category={category}
            categoryLoading={categoryLoading}
            setIsDisableButton={setIsDisableButton}
            selectedSubCategory={selectedSubCategory}
            setSelectedSubCategory={setSelectedSubCategory}
            onReloadDetails={onReloadDetails}
            handleAlaCartOrder={handleAlaCartOrder}
          />
        ) : (
          <PaymentCard
            plates={plates}
            setIsShowPayment={setIsShowPayment}
            onPaymentSuccessReset={onPaymentSuccessReset}
            setIsDisableButton={setIsDisableButton}
            receiptPrice={receiptPrice}
            setReceiptPrice={setReceiptPrice}
            itemDetailsCardRef={itemDetailsCardRef}
            setExtraChargesOptions={setExtraChargesOptions}
            connectDevice={connectDevice}
            setConnectDevice={setConnectDevice}
          />
        )}
      </Col>
      <Col span={8}>
        <SummaryCard
          plates={plates}
          setPlates={setPlates}
          itemDetailsCardRef={itemDetailsCardRef}
          selectedPlateIndex={selectedPlateIndex}
          setSelectedPlateIndex={setSelectedPlateIndex}
          setActiveTabKey={setActiveTabKey}
          selectedItemKey={selectedItemKey}
          setSelectedItemKey={setSelectedItemKey}
          setIsShowPayment={setIsShowPayment}
          isDisableButton={isDisableButton}
          setIsDisableButton={setIsDisableButton}
          receiptPrice={receiptPrice}
          sstInfo={sstInfo}
          setSelectedSubCategory={setSelectedSubCategory}
          setExtraChargesOptions={setExtraChargesOptions}
          extraChargesOptions={extraChargesOptions}
          extraCharges={extraCharges}
        />
      </Col>
    </Row>
  );
}

export default HomePage;
